import { hash } from '@ember/helper';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismCard, PrismTabsContainer } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { and, eq, not } from 'ember-truth-helpers';
import GeneralButtonsOtbSelfServiceButton from 'lumen/components/general/buttons/otb-self-service-button';
import GeneralChartsChartUpdater from 'lumen/components/general/charts/chart-updater';
import HotelOverviewDashboardTilesTopInsightsRatesChart from 'lumen/components/hotel/overview-dashboard/tiles/top-insights/rates-chart';
import HotelOverviewDashboardTilesTopInsightsTabDailyPatterns from 'lumen/components/hotel/overview-dashboard/tiles/top-insights/tab-daily-patterns';
import HotelOverviewDashboardTilesTopInsightsTabRatesChart from 'lumen/components/hotel/overview-dashboard/tiles/top-insights/tab-rates-chart';
import HotelOverviewDashboardUpdateIndicator from 'lumen/components/hotel/overview-dashboard/update-indicator';
import hasFeature from 'lumen/helpers/has-feature';
import {
  OTB_SS_ENTRY_POINTS,
  OTB_SS_VIA_QP_VALUES,
} from 'lumen/utils/constants/otb-selfservice-constants';
import {
  OD_SELECTED_TILES_IDS,
  TOP_INSIGHT_CHART_TABS,
} from 'lumen/utils/constants/overview-dashboard-constants';

interface HotelOverviewDashboardTilesTopInsightsPerformanceRatesTabContainerSignature {
  Args: {
    dailyPatternsOtbData: unknown;
    dailyPatternsPriceOpportunityData: unknown;
    dailyPatternsPricePositionData: unknown;
    dailyPatternsUpdatedIndicatorContent: unknown;
    datePickerRange: unknown;
    isDailyPatternsLoading: unknown;
    isDailyPatternsUpdateIndicatorVisible: unknown;
    isMarketLoading: unknown;
    isRatesLoading: unknown;
    numberOfCompetitors: unknown;
    otaId: unknown;
    otbData: unknown;
    priceDifferenceData: unknown;
    rangeChanged: unknown;
    rentalData: unknown;
    selectedRefDaysAgo: unknown;
    selectedTab: unknown;
    toggleOrSetSelectedDay: unknown;
    updateTopInsightChartTab: unknown;
  };
}

export default class HotelOverviewDashboardTilesTopInsightsPerformanceRatesTabContainer extends Component<HotelOverviewDashboardTilesTopInsightsPerformanceRatesTabContainerSignature> {
  @service declare access: Services['access'];
  @service declare intl: Services['intl'];
  @service declare myotbSs: Services['myotb-ss'];
  @service declare odAccess: Services['od-access'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get cardTileTitle() {
    if (this.visibleTabIds.length === 1) {
      return this.intl.t('overview_dashboard.graph.header.rates');
    }

    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isPerformanceChartVisible() {
    // We do not show Performance tab in case the sub belongs to exclusion list i.e. no OTB integration possible
    return (
      this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.my_otb) ||
      this.access.hasFeature('otb.empty_placeholder')
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get visibleTabIds() {
    const ids = [];

    // We do not show Performance tab in case the sub belongs to exclusion list i.e. no OTB integration possible
    if (this.isPerformanceChartVisible) {
      ids.push(TOP_INSIGHT_CHART_TABS.PERFORMANCE);
    }

    ids.push(TOP_INSIGHT_CHART_TABS.RATES);

    return ids;
  }

  <template>
    {{! template-lint-disable no-duplicate-id }}
    <GeneralChartsChartUpdater as |chartUpdater|>
      {{#if this.cardTileTitle}}
        <PrismCard
          @contentLayout="noPadding"
          class="prism-card--od-performance-rates-tab-container"
        >
          <:header as |Header|>
            <Header.title>
              {{this.cardTileTitle}}
            </Header.title>
          </:header>

          <:content>
            <div class="od-performance-rates-content">
              {{#if (eq this.visibleTabIds.length 1)}}
                <HotelOverviewDashboardTilesTopInsightsRatesChart
                  @isLoading={{@isRatesLoading}}
                  @isPerformanceChartVisible={{this.isPerformanceChartVisible}}
                  @onUpdateChartReference={{chartUpdater.onUpdateChartReference}}
                  {{! @glint-expect-error: Incorrect type }}
                  @otaId={{@otaId}}
                  @otbData={{@otbData}}
                  @priceDifferenceData={{@priceDifferenceData}}
                  @priceOpportunityData={{@dailyPatternsPriceOpportunityData}}
                  @rangeChanged={{@rangeChanged}}
                  @rentalData={{@rentalData}}
                  @selectedRange={{@datePickerRange}}
                  @selectedRefDaysAgo={{@selectedRefDaysAgo}}
                  @toggleOrSetSelectedDay={{@toggleOrSetSelectedDay}}
                />
              {{else}}
                <PrismTabsContainer
                  {{! @glint-expect-error: Incorrect type }}
                  @activeTabId={{@selectedTab}}
                  {{! @glint-expect-error: Incorrect type }}
                  @onSelectTab={{@updateTopInsightChartTab}}
                  @visibleTabIds={{this.visibleTabIds}}
                  as |TabsContainer|
                >
                  <TabsContainer.header>
                    <:default as |Header|>
                      <Header.tab @id={{TOP_INSIGHT_CHART_TABS.PERFORMANCE}}>
                        {{t "overview_dashboard.graph.header.performance"}}
                        {{#if @isDailyPatternsUpdateIndicatorVisible}}
                          <div class="ml-100 flex items-center">
                            <HotelOverviewDashboardUpdateIndicator
                              @updateIndicatorContent={{if
                                @isDailyPatternsUpdateIndicatorVisible
                                @dailyPatternsUpdatedIndicatorContent
                                null
                              }}
                            />
                          </div>
                        {{/if}}
                      </Header.tab>
                      <Header.tab
                        @id={{TOP_INSIGHT_CHART_TABS.RATES}}
                        data-event-id="tab-switch_rates-graph"
                        data-event-value={{TOP_INSIGHT_CHART_TABS.RATES}}
                      >
                        {{t "overview_dashboard.graph.header.rates"}}
                      </Header.tab>
                    </:default>
                    <:after>
                      {{#if
                        (and
                          (not this.myotbSs.isOtbSelfServiceUpsellBannerVisible)
                          (hasFeature "otb.empty_placeholder")
                        )
                      }}
                        <GeneralButtonsOtbSelfServiceButton
                          @otbSelfServiceEntryPoint={{OTB_SS_ENTRY_POINTS.OD_PERFORMANCE_RATES_CHART}}
                          @otbSelfServiceEntryProduct={{OTB_SS_VIA_QP_VALUES.OVERVIEW_DASHBOARD}}
                        />
                      {{/if}}
                    </:after>
                  </TabsContainer.header>
                  <TabsContainer.content as |Content|>
                    <Content.tab @id={{TOP_INSIGHT_CHART_TABS.PERFORMANCE}}>
                      <HotelOverviewDashboardTilesTopInsightsTabDailyPatterns
                        @params={{hash
                          dailyPatternsOtbData=@dailyPatternsOtbData
                          isDailyPatternsLoading=@isDailyPatternsLoading
                          isMarketLoading=@isMarketLoading
                          isPerformanceChartVisible=this.isPerformanceChartVisible
                          isRatesLoading=@isRatesLoading
                          numberOfCompetitors=@numberOfCompetitors
                          onUpdateChartReference=chartUpdater.onUpdateChartReference
                          otaId=@otaId
                          otbData=@otbData
                          priceDifferenceData=@priceDifferenceData
                          priceOpportunityData=@dailyPatternsPriceOpportunityData
                          pricePositionData=@dailyPatternsPricePositionData
                          rangeChanged=@rangeChanged
                          rentalData=@rentalData
                          selectedRange=@datePickerRange
                          selectedRefDaysAgo=@selectedRefDaysAgo
                          toggleOrSetSelectedDay=@toggleOrSetSelectedDay
                        }}
                      />
                    </Content.tab>
                    <Content.tab @id={{TOP_INSIGHT_CHART_TABS.RATES}}>
                      <HotelOverviewDashboardTilesTopInsightsTabRatesChart
                        @params={{hash
                          dailyPatternsOtbData=@dailyPatternsOtbData
                          isDailyPatternsLoading=@isDailyPatternsLoading
                          isPerformanceChartVisible=this.isPerformanceChartVisible
                          isRatesLoading=@isRatesLoading
                          numberOfCompetitors=@numberOfCompetitors
                          onUpdateChartReference=chartUpdater.onUpdateChartReference
                          otaId=@otaId
                          otbData=@otbData
                          priceDifferenceData=@priceDifferenceData
                          priceOpportunityData=@dailyPatternsPriceOpportunityData
                          pricePositionData=@dailyPatternsPricePositionData
                          rangeChanged=@rangeChanged
                          rentalData=@rentalData
                          selectedRange=@datePickerRange
                          selectedRefDaysAgo=@selectedRefDaysAgo
                          toggleOrSetSelectedDay=@toggleOrSetSelectedDay
                        }}
                      />
                    </Content.tab>
                  </TabsContainer.content>
                </PrismTabsContainer>
              {{/if}}
            </div>
          </:content>
        </PrismCard>
      {{else}}
        <PrismCard
          @contentLayout="noPadding"
          class="prism-card--od-performance-rates-tab-container"
        >
          <:content>
            <div class="od-performance-rates-content">
              {{#if (eq this.visibleTabIds.length 1)}}
                <HotelOverviewDashboardTilesTopInsightsRatesChart
                  @isLoading={{@isRatesLoading}}
                  @isPerformanceChartVisible={{this.isPerformanceChartVisible}}
                  @onUpdateChartReference={{chartUpdater.onUpdateChartReference}}
                  {{! @glint-expect-error: Incorrect type }}
                  @otaId={{@otaId}}
                  @otbData={{@otbData}}
                  @priceDifferenceData={{@priceDifferenceData}}
                  @priceOpportunityData={{@dailyPatternsPriceOpportunityData}}
                  @rangeChanged={{@rangeChanged}}
                  @rentalData={{@rentalData}}
                  @selectedRange={{@datePickerRange}}
                  @selectedRefDaysAgo={{@selectedRefDaysAgo}}
                  @toggleOrSetSelectedDay={{@toggleOrSetSelectedDay}}
                />
              {{else}}
                <PrismTabsContainer
                  {{! @glint-expect-error: Incorrect type }}
                  @activeTabId={{@selectedTab}}
                  {{! @glint-expect-error: Incorrect type }}
                  @onSelectTab={{@updateTopInsightChartTab}}
                  @visibleTabIds={{this.visibleTabIds}}
                  as |TabsContainer|
                >
                  <TabsContainer.header>
                    <:default as |Header|>
                      <Header.tab @id={{TOP_INSIGHT_CHART_TABS.PERFORMANCE}}>
                        {{t "overview_dashboard.graph.header.performance"}}
                        {{#if @isDailyPatternsUpdateIndicatorVisible}}
                          <div class="ml-100 flex items-center">
                            <HotelOverviewDashboardUpdateIndicator
                              @updateIndicatorContent={{if
                                @isDailyPatternsUpdateIndicatorVisible
                                @dailyPatternsUpdatedIndicatorContent
                                null
                              }}
                            />
                          </div>
                        {{/if}}
                      </Header.tab>
                      <Header.tab
                        @id={{TOP_INSIGHT_CHART_TABS.RATES}}
                        data-event-id="tab-switch_rates-graph"
                        data-event-value={{TOP_INSIGHT_CHART_TABS.RATES}}
                      >
                        {{t "overview_dashboard.graph.header.rates"}}
                      </Header.tab>
                    </:default>
                    <:after>
                      {{#if
                        (and
                          (not this.myotbSs.isOtbSelfServiceUpsellBannerVisible)
                          (hasFeature "otb.empty_placeholder")
                        )
                      }}
                        <GeneralButtonsOtbSelfServiceButton
                          @otbSelfServiceEntryPoint={{OTB_SS_ENTRY_POINTS.OD_PERFORMANCE_RATES_CHART}}
                          @otbSelfServiceEntryProduct={{OTB_SS_VIA_QP_VALUES.OVERVIEW_DASHBOARD}}
                        />
                      {{/if}}
                    </:after>
                  </TabsContainer.header>
                  <TabsContainer.content as |Content|>
                    <Content.tab @id={{TOP_INSIGHT_CHART_TABS.PERFORMANCE}}>
                      <HotelOverviewDashboardTilesTopInsightsTabDailyPatterns
                        @params={{hash
                          dailyPatternsOtbData=@dailyPatternsOtbData
                          isDailyPatternsLoading=@isDailyPatternsLoading
                          isMarketLoading=@isMarketLoading
                          isPerformanceChartVisible=this.isPerformanceChartVisible
                          isRatesLoading=@isRatesLoading
                          numberOfCompetitors=@numberOfCompetitors
                          onUpdateChartReference=chartUpdater.onUpdateChartReference
                          otaId=@otaId
                          otbData=@otbData
                          priceDifferenceData=@priceDifferenceData
                          priceOpportunityData=@dailyPatternsPriceOpportunityData
                          pricePositionData=@dailyPatternsPricePositionData
                          rangeChanged=@rangeChanged
                          rentalData=@rentalData
                          selectedRange=@datePickerRange
                          selectedRefDaysAgo=@selectedRefDaysAgo
                          toggleOrSetSelectedDay=@toggleOrSetSelectedDay
                        }}
                      />
                    </Content.tab>
                    <Content.tab @id={{TOP_INSIGHT_CHART_TABS.RATES}}>
                      <HotelOverviewDashboardTilesTopInsightsTabRatesChart
                        @params={{hash
                          dailyPatternsOtbData=@dailyPatternsOtbData
                          isDailyPatternsLoading=@isDailyPatternsLoading
                          isPerformanceChartVisible=this.isPerformanceChartVisible
                          isRatesLoading=@isRatesLoading
                          numberOfCompetitors=@numberOfCompetitors
                          onUpdateChartReference=chartUpdater.onUpdateChartReference
                          otaId=@otaId
                          otbData=@otbData
                          priceDifferenceData=@priceDifferenceData
                          priceOpportunityData=@dailyPatternsPriceOpportunityData
                          pricePositionData=@dailyPatternsPricePositionData
                          rangeChanged=@rangeChanged
                          rentalData=@rentalData
                          selectedRange=@datePickerRange
                          selectedRefDaysAgo=@selectedRefDaysAgo
                          toggleOrSetSelectedDay=@toggleOrSetSelectedDay
                        }}
                      />
                    </Content.tab>
                  </TabsContainer.content>
                </PrismTabsContainer>
              {{/if}}
            </div>
          </:content>
        </PrismCard>
      {{/if}}
    </GeneralChartsChartUpdater>
  </template>
}
